import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'default' | 'destructive' | 'outline' | 'ghost' | 'secondary';
type Size = 'default' | 'sm' | 'lg' | 'icon';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const variantStyles: Record<Variant, React.CSSProperties> = {
  default: { background: '#0f172a', color: '#fff' },
  destructive: { background: '#dc2626', color: '#fff' },
  outline: { background: 'transparent', border: '1px solid #cbd5e1' },
  ghost: { background: 'transparent' },
  secondary: { background: '#f1f5f9', color: '#0f172a' },
};

const sizeStyles: Record<Size, React.CSSProperties> = {
  default: { padding: '8px 16px', fontSize: 14 },
  sm: { padding: '6px 12px', fontSize: 13 },
  lg: { padding: '10px 24px', fontSize: 15 },
  icon: { padding: 8, width: 36, height: 36 },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', style, ...props }, ref) => (
    <button
      ref={ref}
      style={{
        ...variantStyles[variant],
        ...sizeStyles[size],
        borderRadius: 6,
        fontWeight: 500,
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        opacity: props.disabled ? 0.6 : 1,
        border: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        ...style,
      }}
      className={cn('btn', className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
